import {
  CheckCircle2,
  FlaskConical,
  ScaleIcon,
  XCircle,
  type LucideIcon,
} from "lucide-react"
import { useTranslation } from "react-i18next"

import { Card } from "@/components/ui/card"
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip"
import { cn } from "@/lib/utils"
import type { LoadDecision, LoadStatus } from "@/features/intake/api/types"
import { stageStepIndex } from "@/features/intake/lib/intake-status"

interface LoadStepperProps {
  status: LoadStatus
  decision?: LoadDecision | null
  // The stage whose panel is currently visible. Defaults to `status`.
  // Drives the "viewed" highlight, which can lag behind the real status
  // when the user is inspecting a previously-completed step.
  viewedStage?: LoadStatus
  // Pure panel selector — no API call. Caller updates local state.
  onJump?: (stage: LoadStatus) => void
}

interface Step {
  key: LoadStatus
  label: string
  Icon: LucideIcon
}

// Mirrors the OT (transit) stepper for consistency. Tiles can be
// clicked to navigate to any already-reached stage; future stages stay
// locked.
export function LoadStepper({
  status,
  decision,
  viewedStage,
  onJump,
}: LoadStepperProps) {
  const { t } = useTranslation("intake")
  const currentIdx = stageStepIndex(status)
  const viewed = viewedStage ?? status
  const refused = decision === "REJECTED"

  const steps: Step[] = [
    { key: "SCALE", label: t("detail.stepper.scale"), Icon: ScaleIcon },
    {
      key: "QUALITY",
      label: t("detail.stepper.quality"),
      Icon: FlaskConical,
    },
    {
      key: "COMPLETED",
      label: t("detail.stepper.completed"),
      Icon: refused ? XCircle : CheckCircle2,
    },
  ]

  return (
    <Card className="p-3">
      <ol className="flex flex-wrap items-stretch gap-2">
        {steps.map((step, idx) => {
          const isDone = idx < currentIdx
          const isCurrentReal = idx === currentIdx
          const isViewed = step.key === viewed
          const reached = idx <= currentIdx
          const clickable = reached && !isViewed && !!onJump
          const isCompletedRefused = step.key === "COMPLETED" && refused

          // Tile background: highlight current / viewed; neutral for
          // done; muted for unreached. Refused completed gets a soft
          // red tint when viewed/current.
          const tile =
            isCurrentReal || isViewed
              ? isCompletedRefused
                ? "border-red-300 bg-red-50 text-foreground dark:border-red-500/40 dark:bg-red-500/10"
                : "border-primary/60 bg-primary/5 text-foreground"
              : isDone
                ? "border-border bg-card text-foreground"
                : "border-border bg-muted/40 text-muted-foreground"

          // Inner ring tone.
          const ring =
            isCurrentReal || isViewed
              ? isCompletedRefused
                ? "bg-red-500 text-white"
                : "bg-primary text-primary-foreground"
              : isDone
                ? "bg-emerald-600 text-white"
                : "bg-muted text-muted-foreground"

          return (
            <li key={step.key} className="min-w-0 flex-1 basis-32">
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    onClick={() => clickable && onJump?.(step.key)}
                    disabled={!clickable}
                    className={cn(
                      "relative flex h-11 w-full items-center gap-2 rounded-md border px-3 py-2 text-left transition-colors",
                      tile,
                      clickable && "cursor-pointer hover:bg-muted/30",
                    )}
                    aria-current={isViewed ? "step" : undefined}
                  >
                    <span
                      className={cn(
                        "flex size-6 flex-shrink-0 items-center justify-center rounded-full",
                        ring,
                      )}
                    >
                      <step.Icon className="size-3.5" />
                    </span>
                    <div className="flex min-w-0 flex-1 flex-col justify-center leading-tight">
                      <div className="truncate text-xs font-medium">
                        {step.label}
                      </div>
                      {isCurrentReal ? (
                        <div
                          className={cn(
                            "text-[9px] font-semibold tracking-wider uppercase",
                            isCompletedRefused
                              ? "text-red-600 dark:text-red-400"
                              : "text-primary",
                          )}
                        >
                          {t("detail.stepper.currentBadge", {
                            defaultValue: "Current",
                          })}
                        </div>
                      ) : null}
                    </div>
                  </button>
                </TooltipTrigger>
                <TooltipContent>
                  {clickable
                    ? t("detail.stepper.jumpHint", {
                        stage: step.label,
                        defaultValue: `Go to ${step.label}`,
                      })
                    : isCurrentReal
                      ? t("detail.stepper.activeHint", {
                          defaultValue: "Current step",
                        })
                      : isDone
                        ? t("detail.stepper.doneHint", {
                            defaultValue: "Already completed",
                          })
                        : t("detail.stepper.lockedHint", {
                            defaultValue: "Not yet reached",
                          })}
                </TooltipContent>
              </Tooltip>
            </li>
          )
        })}
      </ol>
    </Card>
  )
}
